import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, ToggleLeft, ToggleRight, Save, X, ChefHat, Package, Image, Trash2, Settings, Download, Upload, FileText, AlertCircle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MediaPicker from '../components/MediaPicker';

import { API_BASE } from '../lib/api';
import { formatPrice, stripHtmlToText } from '../lib/utils';
// @module:shipping-imports
import { useShippingNatures } from '../hooks/useShippingNatures';

interface Product {
  id: number;
  brand_group: string;
  brand: string;
  category: string;
  product_code: string | null;
  name: string;
  description: string | null;
  price: number | null;
  original_price: number | null;
  is_active: number;
  stock_quantity: number;
  sort_order: number;
  image_url: string | null;
  image_url_2: string | null;
  image_url_3: string | null;
  max_select: number;
  spiciness_level: number;
  natureId?: number | null; // shipping-zones v2 物流定性覆寫（null=繼承類目；未裝組件時後端不回）
}

const BRAND_GROUP_OPTIONS = [
  { slug: 'hk_brand', name: '香港品牌' },
  { slug: 'overseas_brand', name: '海外品牌' },
];

interface ProductCategory {
  id: number;
  slug: string;
  name: string;
  sortOrder: number;
  isActive: number;
  natureId?: number | null; // shipping-zones v2 物流定性（未裝組件時後端不回、UI 不用）
}

interface AdminUser {
  role: string;
}

const AdminProducts: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  // @module:shipping-natures
const { natures: shippingNatures } = useShippingNatures();
const natureLabelOf = (natureId: number | null | undefined) =>
  natureId == null ? '' : (shippingNatures.find(n => n.id === natureId)?.nameZh || '');
const natureInputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white';
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeBrandGroup, setActiveBrandGroup] = useState<string>('all');
  const [activeBrand, setActiveBrand] = useState<string>('all');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState<{ mode: 'add' | 'edit'; slot: 'image_url' | 'image_url_2' | 'image_url_3' } | null>(null);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    brand_group: 'hk_brand',
    brand: 'main',
    category: 'spice',
    name: '',
    description: '',
    price: null,
    is_active: 1,
    stock_quantity: 50,
    sort_order: 0,
    spiciness_level: 1,
    image_url: null,
    image_url_2: null,
    image_url_3: null,
  });

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState<Partial<ProductCategory>>({ slug: '', name: '', sortOrder: 0 });
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState<Partial<Product>[] | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ total: number; successCount: number; failedCount: number; errors: { row: number; message: string }[] } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const token = localStorage.getItem('admin_token');
  const storedUser = localStorage.getItem('admin_user');
  const user: AdminUser | null = storedUser ? JSON.parse(storedUser) : null;
  const isSupplier = user?.role === 'supplier';

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchProducts();
    fetchCategories();
  }, [token, navigate]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/public/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setProducts(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取產品失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/product-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (e) {
      console.error('獲取類別失敗', e);
    }
  };

  const exportProductsCSV = () => {
    const headers = ['brand_group', 'brand', 'category', 'product_code', 'name', 'description', 'price', 'original_price', 'stock_quantity', 'low_stock_threshold', 'image_url', 'image_url_2', 'image_url_3', 'spiciness_level', 'is_active', 'sort_order', 'max_select'];
    const rows = sortedProducts.map(p => [
      p.brand_group,
      p.brand,
      p.category,
      p.product_code || '',
      p.name,
      p.description || '',
      p.price ?? '',
      p.original_price ?? '',
      p.stock_quantity,
      10,
      p.image_url || '',
      p.image_url_2 || '',
      p.image_url_3 || '',
      p.spiciness_level,
      p.is_active,
      p.sort_order,
      p.max_select,
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          cell += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          cell += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          row.push(cell);
          cell = '';
        } else if (char === '\n' || char === '\r') {
          if (char === '\r' && nextChar === '\n') i++;
          if (row.length > 0 || cell !== '') {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
          }
        } else {
          cell += char;
        }
      }
    }

    if (row.length > 0 || cell !== '') {
      row.push(cell);
      rows.push(row);
    }

    return rows;
  };

  const normalizeHeader = (h: string): string => h.toLowerCase().trim().replace(/\s+/g, '_');

  const parseImportCSV = (text: string): { products: Partial<Product>[]; errors: string[] } => {
    const rows = parseCSV(text.trim());
    if (rows.length < 2) return { products: [], errors: ['CSV 沒有資料行'] };

    const headers = rows[0].map(normalizeHeader);
    const required = ['name', 'price'];
    const missing = required.filter(k => !headers.includes(k));
    if (missing.length > 0) {
      return { products: [], errors: [`缺少必填欄位：${missing.join(', ')}`] };
    }

    const get = (cells: string[], key: string): string | undefined => {
      const idx = headers.indexOf(key);
      return idx >= 0 ? cells[idx]?.trim() : undefined;
    };

    const getNum = (cells: string[], key: string): number | undefined => {
      const val = get(cells, key);
      if (val === undefined || val === '') return undefined;
      const n = Number(val.replace(/,/g, ''));
      return Number.isFinite(n) ? n : undefined;
    };

    const products: Partial<Product>[] = [];
    const errors: string[] = [];

    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i];
      if (cells.every(c => c.trim() === '')) continue;

      const name = get(cells, 'name');
      const price = getNum(cells, 'price');

      if (!name) {
        errors.push(`第 ${i} 行：name 為必填`);
        continue;
      }
      if (price === undefined || price < 0) {
        errors.push(`第 ${i} 行：price 必須為有效數字`);
        continue;
      }

      products.push({
        brand_group: get(cells, 'brand_group') || 'hk_brand',
        brand: get(cells, 'brand') || 'main',
        category: get(cells, 'category') || 'spice',
        product_code: get(cells, 'product_code') || get(cells, 'sku') || undefined,
        name,
        description: get(cells, 'description') || undefined,
        price,
        original_price: getNum(cells, 'original_price'),
        stock_quantity: getNum(cells, 'stock_quantity') ?? 0,
        sort_order: getNum(cells, 'sort_order') ?? 0,
        image_url: get(cells, 'image_url') || undefined,
        image_url_2: get(cells, 'image_url_2') || undefined,
        image_url_3: get(cells, 'image_url_3') || undefined,
        max_select: getNum(cells, 'max_select') ?? 10,
        spiciness_level: getNum(cells, 'spiciness_level') ?? 1,
        is_active: get(cells, 'is_active') === '0' || get(cells, 'is_active')?.toLowerCase() === 'false' ? 0 : 1,
      });
    }

    return { products, errors };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || '');
      setImportText(text);
      const { products, errors } = parseImportCSV(text);
      setImportPreview(products);
      setImportErrors(errors);
      setImportResult(null);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importPreview || importPreview.length === 0) return;
    setIsImporting(true);
    setImportResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ products: importPreview }),
      });
      const data = await res.json();
      if (data.success) {
        setImportResult(data);
        fetchProducts();
      } else {
        setError(data.error || '匯入失敗');
      }
    } catch (e) {
      setError('匯入請求失敗');
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportText('');
    setImportPreview(null);
    setImportErrors([]);
    setImportResult(null);
  };

  const renderImageField = (
    mode: 'add' | 'edit',
    slot: 'image_url' | 'image_url_2' | 'image_url_3',
    label: string
  ) => {
    const isAdd = mode === 'add';
    const value = isAdd ? newProduct[slot] : editForm[slot];
    const setter = isAdd ? setNewProduct : setEditForm;
    const targetObj = isAdd ? newProduct : editForm;
    return (
      <div className="md:col-span-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <div className="flex gap-2">
          <input
            value={value || ''}
            onChange={e => setter({ ...targetObj, [slot]: e.target.value })}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
            placeholder="https://...（可從媒體庫選擇）"
          />
          <button
            type="button"
            onClick={() => setMediaPickerTarget({ mode, slot })}
            className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
          >
            <Image size={16} className="inline mr-1" /> 媒體庫
          </button>
        </div>
        {value && (
          <img src={value} alt={label} className="mt-2 max-h-32 rounded border border-gray-200 dark:border-gray-700 object-contain" />
        )}
      </div>
    );
  };

  const categoryLabels = useMemo(() => {
    const map: Record<string, { label: string; icon: React.ElementType; color: string }> = {};
    categories.forEach((c) => {
      map[c.slug] = { label: c.name, icon: c.slug === 'spice' ? ChefHat : Package, color: 'text-red-600 bg-red-50' };
    });
    return map;
  }, [categories]);

  const brandGroupLabels = useMemo(() => {
    const map: Record<string, string> = {};
    BRAND_GROUP_OPTIONS.forEach((b) => { map[b.slug] = b.name; });
    return map;
  }, []);

  const brandGroupTabs = useMemo(() => {
    const list = [{ key: 'all', label: '全部品牌群組' }];
    BRAND_GROUP_OPTIONS.forEach((b) => list.push({ key: b.slug, label: b.name }));
    return list;
  }, []);

  const brandNameOptions = useMemo(() => {
    const set = new Set<string>();
    if (activeBrandGroup !== 'all') {
      products.filter(p => p.brand_group === activeBrandGroup).forEach(p => set.add(p.brand));
    } else {
      products.forEach(p => set.add(p.brand));
    }
    return Array.from(set).sort();
  }, [products, activeBrandGroup]);

  const brandNameTabs = useMemo(() => {
    const list = [{ key: 'all', label: '全部品牌' }];
    brandNameOptions.forEach((b) => list.push({ key: b, label: b }));
    return list;
  }, [brandNameOptions]);

  const categoryTabs = useMemo(() => {
    const list = [{ key: 'all', label: '全部類別' }];
    categories.forEach((c) => list.push({ key: c.slug, label: c.name }));
    return list;
  }, [categories]);

  const handleToggle = async (id: number, current: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/${id}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !current }),
      });
      if (res.ok) fetchProducts();
    } catch (e) {
      setError('操作失敗');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingId(product.id);
    setEditForm({
      ...product,
      price: product.price ? product.price / 100 : null,
    });
  };

  const handleSave = async () => {
    if (!editForm.name) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        setEditingId(null);
        fetchProducts();
      }
    } catch (e) {
      setError('保存失敗');
    }
  };

  const handleAdd = async () => {
    if (!newProduct.name) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newProduct),
      });
      if (res.ok) {
        setShowAddForm(false);
        setNewProduct({ brand_group: 'hk_brand', brand: 'main', category: 'spice', name: '', description: '', price: null, is_active: 1, stock_quantity: 50, sort_order: 0, spiciness_level: 1, image_url: null, image_url_2: null, image_url_3: null });
        fetchProducts();
      }
    } catch (e) {
      setError('創建失敗');
    }
  };

  const sortedProducts = [...products].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const filteredProducts = sortedProducts.filter((p) => {
    const brandGroupMatch = activeBrandGroup === 'all' || p.brand_group === activeBrandGroup;
    const brandMatch = activeBrand === 'all' || p.brand === activeBrand;
    const categoryMatch = activeCategory === 'all' || p.category === activeCategory;
    return brandGroupMatch && brandMatch && categoryMatch;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeBrandGroup, activeBrand, activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProducts = filteredProducts.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  const handleSaveCategory = async () => {
    if (!categoryForm.slug || !categoryForm.name) {
      setError('請填寫類別代號與名稱');
      return;
    }
    try {
      const url = editingCategoryId
        ? `${API_BASE}/api/public/admin/product-categories/${editingCategoryId}`
        : `${API_BASE}/api/public/admin/product-categories`;
      const method = editingCategoryId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(categoryForm),
      });
      if (res.ok) {
        setCategoryForm({ slug: '', name: '', sortOrder: 0 });
        setEditingCategoryId(null);
        fetchCategories();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存類別失敗');
      }
    } catch (e) {
      setError('儲存類別失敗');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!confirm('確定刪除此類別？已有的產品不會被刪除，但可能無法正確顯示。')) return;
    try {
      const res = await fetch(`${API_BASE}/api/public/admin/product-categories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchCategories();
    } catch (e) {
      setError('刪除類別失敗');
    }
  };

  const startEditCategory = (cat: ProductCategory) => {
    setEditingCategoryId(cat.id);
    setCategoryForm({ slug: cat.slug, name: cat.name, sortOrder: cat.sortOrder });
  };

  return (
    <AdminLayout currentPage="products">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Brand Group Tabs */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-2 rounded-t-lg">
          <nav className="flex space-x-1 px-2 overflow-x-auto" aria-label="BrandGroupTabs">
            {brandGroupTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveBrandGroup(tab.key); setActiveBrand('all'); }}
                className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeBrandGroup === tab.key
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Brand Name Tabs */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-2">
          <nav className="flex space-x-1 px-2 overflow-x-auto" aria-label="BrandNameTabs">
            {brandNameTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveBrand(tab.key)}
                className={`py-2 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeBrand === tab.key
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Category Tabs */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-6">
          <nav className="flex space-x-1 px-2 overflow-x-auto" aria-label="CategoryTabs">
            {categoryTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveCategory(tab.key)}
                className={`py-2 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeCategory === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Actions */}
        {!isSupplier && (
          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => {
                if (!showAddForm) {
                  const maxOrder = products.reduce((max, p) => Math.max(max, p.sort_order || 0), 0);
                  setNewProduct({
                    brand_group: 'hk_brand',
                    brand: 'main',
                    category: categories[0]?.slug || 'spice',
                    name: '',
                    description: '',
                    price: null,
                    is_active: 1,
                    stock_quantity: 50,
                    sort_order: maxOrder + 10,
                    spiciness_level: 1,
                    image_url: null,
                    image_url_2: null,
                    image_url_3: null,
                  });
                }
                setShowAddForm(!showAddForm);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {showAddForm ? '取消' : '新增產品'}
            </button>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <Settings className="w-5 h-5 mr-2" />
              管理類別
            </button>
            <button
              onClick={exportProductsCSV}
              disabled={products.length === 0}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Download className="w-5 h-5 mr-2" />
              匯出 CSV
            </button>
            <button
              onClick={() => { setShowImportModal(true); resetImport(); }}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              <Upload className="w-5 h-5 mr-2" />
              匯入 CSV
            </button>
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
            <h3 className="text-lg font-bold mb-4 dark:text-white">新增產品</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">品牌群組</label>
                <select
                  value={newProduct.brand_group}
                  onChange={e => setNewProduct({ ...newProduct, brand_group: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                >
                  {BRAND_GROUP_OPTIONS.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">品牌</label>
                <input
                  value={newProduct.brand || ''}
                  onChange={e => setNewProduct({ ...newProduct, brand: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="品牌（例：main）"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類別</label>
                <select
                  value={newProduct.category}
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                >
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              {/* @module:product-form-nature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">物流定性</label>
                <select
                  value={(newProduct as Record<string, unknown>).natureId != null ? String((newProduct as Record<string, unknown>).natureId) : ''}
                  onChange={e => setNewProduct({ ...newProduct, natureId: e.target.value ? Number(e.target.value) : null } as Partial<Product>)}
                  className={natureInputCls}
                >
                  <option value="">{`繼承類目${natureLabelOf(categories.find(c => c.slug === newProduct.category)?.natureId) ? `（${natureLabelOf(categories.find(c => c.slug === newProduct.category)?.natureId)}）` : ''}`}</option>
                  {shippingNatures.map(n => <option key={n.id} value={n.id}>{n.nameZh}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">辣度等級</label>
                <input
                  type="number"
                  min={0}
                  max={5}
                  value={newProduct.spiciness_level ?? 1}
                  onChange={e => setNewProduct({ ...newProduct, spiciness_level: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名稱</label>
                <input
                  value={newProduct.name}
                  onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="產品名稱"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">價格 (HK$)</label>
                <input
                  type="number"
                  value={newProduct.price || ''}
                  onChange={e => setNewProduct({ ...newProduct, price: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="HK$"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  描述 <span className="text-xs font-normal text-gray-500">（支援 HTML，例如 &lt;br&gt;、&lt;strong&gt;、&lt;ul&gt; 等）</span>
                </label>
                <textarea
                  value={newProduct.description || ''}
                  onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="產品描述，可使用 HTML 標籤換行或排版"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input
                  type="number"
                  value={newProduct.sort_order || 0}
                  onChange={e => setNewProduct({ ...newProduct, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">庫存</label>
                <input
                  type="number"
                  value={newProduct.stock_quantity ?? ''}
                  onChange={e => setNewProduct({ ...newProduct, stock_quantity: e.target.value ? parseInt(e.target.value) : 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="數量"
                />
              </div>
              {renderImageField('add', 'image_url', '圖片 1 URL')}
              {renderImageField('add', 'image_url_2', '圖片 2 URL（可選）')}
              {renderImageField('add', 'image_url_3', '圖片 3 URL（可選）')}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">創建</button>
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
            </div>
          </div>
        )}

        {/* Products Table */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            <table className="min-w-[1200px] w-full table-fixed">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">排序</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">品牌群組</th>
                  <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">品牌</th>
                  <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">類別</th>
                  {/* @module:product-nature-head */}
                  <th className="w-24 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">定性</th>
                  <th className="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">辣度</th>
                  <th className="w-28 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">編號</th>
                  <th className="w-40 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">名稱</th>
                  <th className="w-56 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">描述</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">價格</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">庫存</th>
                  <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">圖片</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">狀態</th>
                  <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedProducts.map(product => {
                  const cat = categoryLabels[product.category] || { label: product.category, icon: Package, color: 'text-gray-600 bg-gray-100' };
                  const CatIcon = cat.icon;
                  const isEditing = editingId === product.id;

                  return (
                    <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.sort_order ?? 0}
                            onChange={e => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-bold text-gray-600 dark:text-gray-300">
                            {product.sort_order || 0}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={editForm.brand_group || 'hk_brand'}
                            onChange={e => setEditForm({ ...editForm, brand_group: e.target.value })}
                            className="w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          >
                            {BRAND_GROUP_OPTIONS.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                          </select>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-200">
                            {brandGroupLabels[product.brand_group] || product.brand_group}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            value={editForm.brand || ''}
                            onChange={e => setEditForm({ ...editForm, brand: e.target.value })}
                            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            placeholder="品牌"
                          />
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                            {product.brand}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={editForm.category || ''}
                            onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                            className="w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          >
                            <option value="">選擇類別</option>
                            {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                          </select>
                        ) : (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${cat.color}`}>
                            <CatIcon size={14} />
                            {cat.label}
                          </span>
                        )}
                      </td>
                      {/* @module:product-nature-cell */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={editForm.natureId != null ? String(editForm.natureId) : ''}
                            onChange={e => setEditForm({ ...editForm, natureId: e.target.value ? Number(e.target.value) : null } as Partial<Product>)}
                            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            title="物流定性覆寫（空＝繼承類目）"
                          >
                            <option value="">繼承類目</option>
                            {shippingNatures.map(n => <option key={n.id} value={n.id}>{n.nameZh}</option>)}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {(() => {
                              const catNid = categories.find(c => c.slug === product.category)?.natureId ?? null;
                              const nid = product.natureId ?? catNid;
                              if (nid == null) return '—';
                              return (product.natureId != null ? '※ ' : '') + natureLabelOf(nid);
                            })()}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            max={5}
                            value={editForm.spiciness_level ?? 0}
                            onChange={e => setEditForm({ ...editForm, spiciness_level: parseInt(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-50 text-orange-700 dark:bg-orange-900 dark:text-orange-200">
                            {product.spiciness_level}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap min-w-[8rem]">
                        {isEditing ? (
                          <input
                            value={editForm.product_code || ''}
                            onChange={e => setEditForm({ ...editForm, product_code: e.target.value })}
                            className="w-28 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            placeholder="編號"
                          />
                        ) : (
                          <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">{product.product_code || '-'}</code>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top overflow-hidden">
                        {isEditing ? (
                          <input
                            value={editForm.name || ''}
                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className="font-medium text-gray-900 dark:text-white line-clamp-2 break-words" title={product.name}>{product.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 align-top overflow-hidden">
                        {isEditing ? (
                          <textarea
                            value={editForm.description || ''}
                            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                            rows={3}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white resize-y"
                          />
                        ) : (
                          <span className="line-clamp-3 break-words" title={product.description ? stripHtmlToText(product.description) : undefined}>{product.description ? stripHtmlToText(product.description) : '-'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.price || ''}
                            onChange={e => setEditForm({ ...editForm, price: e.target.value ? parseInt(e.target.value) : null })}
                            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          product.price ? formatPrice(product.price) : '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.stock_quantity ?? 0}
                            onChange={e => setEditForm({ ...editForm, stock_quantity: parseInt(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className={`font-medium ${
                            product.stock_quantity === 0 ? 'text-red-600' :
                            product.stock_quantity < 15 ? 'text-red-500' : 'text-green-600'
                          }`}>
                            {product.stock_quantity}
                            {product.stock_quantity === 0 && <span className="ml-1 text-xs">(缺貨)</span>}
                            {product.stock_quantity > 0 && product.stock_quantity < 15 && <span className="ml-1 text-xs">(即將售罄)</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm min-w-[12rem] whitespace-nowrap">
                        {isEditing ? (
                          <div className="space-y-2">
                            {(['image_url', 'image_url_2', 'image_url_3'] as const).map((slot, idx) => (
                              <div key={slot} className="flex gap-2">
                                <input
                                  value={(editForm[slot] as string | null) || ''}
                                  onChange={e => setEditForm({ ...editForm, [slot]: e.target.value })}
                                  className="flex-1 min-w-[10rem] px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                                  placeholder={`圖片 ${idx + 1} URL`}
                                />
                                <button
                                  type="button"
                                  onClick={() => setMediaPickerTarget({ mode: 'edit', slot })}
                                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm"
                                >
                                  <Image size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            {[product.image_url, product.image_url_2, product.image_url_3].filter(Boolean).map((url, idx) => (
                              <img key={idx} src={url!} alt={`${product.name} ${idx + 1}`} className="w-10 h-10 object-cover rounded" />
                            ))}
                            {!product.image_url && !product.image_url_2 && !product.image_url_3 && (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => !isEditing && handleToggle(product.id, product.is_active)}
                          className={`flex items-center gap-1 text-sm font-medium ${product.is_active ? 'text-green-600' : 'text-gray-400'}`}
                        >
                          {product.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          {product.is_active ? '啟用' : '停用'}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button onClick={handleSave} className="text-green-600 hover:text-green-800"><Save size={18} /></button>
                            <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                          </div>
                        ) : (
                          <button onClick={() => handleEdit(product)} className="text-blue-600 hover:text-blue-800">
                            <Edit2 size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  顯示第 {((safePage - 1) * itemsPerPage) + 1} – {Math.min(safePage * itemsPerPage, filteredProducts.length)} 筆，共 {filteredProducts.length} 筆
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="p-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="上一頁"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {safePage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="p-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="下一頁"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {filteredProducts.length === 0 && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">沒有產品</div>
            )}
          </div>
        )}

        {/* Media Picker */}
        {mediaPickerTarget && (
          <MediaPicker
            category="product"
            selectedUrl={(mediaPickerTarget.mode === 'add' ? newProduct[mediaPickerTarget.slot] : editForm[mediaPickerTarget.slot]) || undefined}
            onSelect={(url) => {
              const targetObj = mediaPickerTarget.mode === 'add' ? newProduct : editForm;
              const setter = mediaPickerTarget.mode === 'add' ? setNewProduct : setEditForm;
              setter({ ...targetObj, [mediaPickerTarget.slot]: url });
              setMediaPickerTarget(null);
            }}
            onClose={() => setMediaPickerTarget(null)}
          />
        )}

        {/* Category Management Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold dark:text-white">管理產品類別</h3>
                <button onClick={() => setShowCategoryModal(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400"><X size={20} /></button>
              </div>

              <div className="space-y-3 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    placeholder="類別代號 (slug)"
                    value={categoryForm.slug || ''}
                    disabled={!!editingCategoryId}
                    onChange={e => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  />
                  <input
                    placeholder="顯示名稱"
                    value={categoryForm.name || ''}
                    onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    type="number"
                    placeholder="排序"
                    value={categoryForm.sortOrder ?? 0}
                    onChange={e => setCategoryForm({ ...categoryForm, sortOrder: parseInt(e.target.value) || 0 })}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  />
                  {/* @module:category-form-nature */}
                  <select
                    value={(categoryForm as Record<string, unknown>).natureId != null ? String((categoryForm as Record<string, unknown>).natureId) : ''}
                    onChange={e => setCategoryForm({ ...categoryForm, natureId: e.target.value ? Number(e.target.value) : null } as Partial<ProductCategory>)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                    title="物流定性：該類商品的跨境可寄屬性（空＝其他）"
                  >
                    <option value="">物流定性（空＝其他）</option>
                    {shippingNatures.map(n => <option key={n.id} value={n.id}>{n.nameZh}</option>)}
                  </select>
                  <button onClick={handleSaveCategory} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {editingCategoryId ? '更新' : '新增'}
                  </button>
                  {editingCategoryId && (
                    <button onClick={() => { setEditingCategoryId(null); setCategoryForm({ slug: '', name: '', sortOrder: 0 }); }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
                      取消
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 py-2 text-left">代號</th>
                      <th className="px-3 py-2 text-left">名稱</th>
                      <th className="px-3 py-2 text-left">排序</th>
                      <th className="px-3 py-2 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {categories.map(cat => (
                      <tr key={cat.id}>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{cat.slug}</td>
                        <td className="px-3 py-2 font-medium dark:text-white">{cat.name}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{cat.sortOrder}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => startEditCategory(cat)} className="text-blue-600 hover:text-blue-800 mr-2"><Edit2 size={16} /></button>
                          <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-600 hover:text-red-800"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {categories.length === 0 && (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">尚無類別</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-600" />
                  匯入產品 CSV
                </h3>
                <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400"><X size={20} /></button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">必填欄位：name、price</p>
                  <p>建議先 <a href="/product_import_template.csv" target="_blank" className="underline hover:text-blue-600">下載藍本 CSV</a> 填寫。圖片請先上傳到媒體庫，再貼上 <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">/media/...</code> 路徑。</p>
                </div>

                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="product-csv-upload"
                  />
                  <label
                    htmlFor="product-csv-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FileText className="w-10 h-10 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {importFile ? importFile.name : '點擊選擇 CSV 檔案'}
                    </span>
                  </label>
                </div>

                {importErrors.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-200 flex items-center gap-1 mb-2">
                      <AlertCircle className="w-4 h-4" /> 解析問題
                    </h4>
                    <ul className="text-xs text-red-700 dark:text-red-300 max-h-32 overflow-y-auto space-y-1">
                      {importErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                    </ul>
                  </div>
                )}

                {importResult && (
                  <div className={`border rounded-lg p-3 ${importResult.failedCount === 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'}`}>
                    <h4 className="text-sm font-medium flex items-center gap-1 mb-2 dark:text-white">
                      {importResult.failedCount === 0 ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-yellow-600" />}
                      匯入結果
                    </h4>
                    <p className="text-sm dark:text-gray-200">
                      共 {importResult.total} 行，成功 <span className="font-bold text-green-600">{importResult.successCount}</span> 行，失敗 <span className="font-bold text-red-600">{importResult.failedCount}</span> 行。
                    </p>
                    {importResult.errors.length > 0 && (
                      <ul className="text-xs text-red-700 dark:text-red-300 max-h-40 overflow-y-auto mt-2 space-y-1">
                        {importResult.errors.map((err, idx) => <li key={idx}>第 {err.row} 行：{err.message}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                {importPreview && importPreview.length > 0 && !importResult && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">預覽（前 10 筆）</h4>
                    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th className="px-2 py-1 text-left">品牌群組</th>
                            <th className="px-2 py-1 text-left">品牌</th>
                            <th className="px-2 py-1 text-left">類別</th>
                            <th className="px-2 py-1 text-left">編號</th>
                            <th className="px-2 py-1 text-left">名稱</th>
                            <th className="px-2 py-1 text-left">價格</th>
                            <th className="px-2 py-1 text-left">辣度</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:text-gray-200">
                          {importPreview.slice(0, 10).map((p, idx) => (
                            <tr key={idx}>
                              <td className="px-2 py-1">{p.brand_group}</td>
                              <td className="px-2 py-1">{p.brand}</td>
                              <td className="px-2 py-1">{p.category}</td>
                              <td className="px-2 py-1">{p.product_code || '-'}</td>
                              <td className="px-2 py-1">{p.name}</td>
                              <td className="px-2 py-1">HK${p.price}</td>
                              <td className="px-2 py-1">{p.spiciness_level}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {importPreview.length > 10 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">還有 {importPreview.length - 10} 筆未顯示</p>
                    )}
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    關閉
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importPreview || importPreview.length === 0 || isImporting}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isImporting ? '匯入中...' : `確認匯入 ${importPreview ? `(${importPreview.length} 筆)` : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
